/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import {
  Authentication,
  FlowNode,
  isNode,
  Onboarding,
  WorkflowDTO,
} from '@facephi/sdk-web';
import { cloneDeep } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import workFlowJson from '../assets/json/workflow.json';
import { useAuth } from '../providers';
import {
  WidgetIdList,
  WidgetNameList,
  DEFAULT_COUNTRY,
  TypeDocument,
  WidgetDocumentType,
} from '../states';
import { antispoofingState, widgetLogState } from '../states/atoms';

type Widgets = {
  [key in WidgetNameList]: Authentication | Onboarding;
};

type IConfig = {
  showLog?: boolean;
  logImages: boolean;
  previewCapture: boolean;
  country: string;
  licenseKey?: string;
  documentType?: string;
  retryOnlyCurrentSide: boolean;
  antispoofEnabled?: boolean;
};

type OnboardingDemo = Onboarding & {
  cameraHeight?: number;
  cameraWidth?: number;
};

export function useWorkflow(): {
  loading: boolean;
  config: Authentication & OnboardingDemo;
  getWorkflow(config: IConfig): WorkflowDTO;
} {
  const { i18n } = useTranslation();
  const { hasBackendAnalysis } = useAuth();
  const [workflow] = useState<WorkflowDTO>(
    workFlowJson.data.flow.definition as WorkflowDTO
  );

  const [loading, setLoading] = useState<boolean>(true);
  const [config, setConfig] = useState<Widgets>();
  const antispoofing = useRecoilValue(antispoofingState);
  const widgetLog = useRecoilValue(widgetLogState);

  const getWidgetCofig = (id: string) => {
    const node = workflow.find(
      (item) => isNode(item) && (item as FlowNode).data.widgetId === id
    );
    return (node as FlowNode)?.data?.configuration?.general || {};
  };

  useEffect(() => {
    setConfig({
      [WidgetNameList.selphi]: getWidgetCofig(
        WidgetIdList[WidgetNameList.selphi]
      ) as Authentication,
      [WidgetNameList.selphid]: getWidgetCofig(
        WidgetIdList[WidgetNameList.selphid]
      ) as OnboardingDemo,
    });

    setLoading(false);
  }, [workflow]);

  const getWorkflow = ({
    logImages,
    previewCapture,
    country,
    licenseKey,
    documentType,
    retryOnlyCurrentSide,
  }: IConfig) =>
    workflow.map((item) => {
      if (isNode(item)) {
        const node = cloneDeep(item as FlowNode);
        if (node.data.configuration) {
          node.data.configuration.general = {
            ...node.data.configuration?.general,
            language: i18n.language,
          };
          if (node.data.widgetId === WidgetIdList.selphi) {
            node.data.configuration.general = {
              ...node.data.configuration.general,
              selphiWidgetSettings: {
                // logImages: logImages ? 5 : 0,
                disableTutorial: true,
                interactible: false,
                showLog: widgetLog,
                cropFactor: 1.7,
                antispoof: antispoofing,
                previewImage: previewCapture,
                language: i18n.language,
                advancedTracking: false,
              },
            };
          }
          if (node.data.widgetId === WidgetIdList.selphid) {
            const countryFilter =
              country === DEFAULT_COUNTRY || !country ? undefined : [country];
            const valueDocumentType: any =
              !documentType || documentType === TypeDocument.custom
                ? 0
                : WidgetDocumentType[documentType as any];

            const settings = {
              previewImage: previewCapture,
              licenseKey,
              documentType: [valueDocumentType],
              allowUncertain: !countryFilter ? true : false,
              retryCurrentSide: retryOnlyCurrentSide,
              cameraWidth: 1920,
              cameraHeight: 1080,
              showLog: widgetLog,
              language: i18n.language,
              advancedTracking: false,
              allowUnknown: hasBackendAnalysis,
            };
            node.data.configuration.general = {
              ...node.data.configuration.general,
              // logImages: logImages ? 5 : 0,
              // TO DO REPLACE THIS PATCH WHEN WIDGETS TEAM MAKE ADJUSTMENTS
              selphidWidgetSettings: countryFilter
                ? {
                    ...settings,
                    countryFilter,
                  }
                : settings,
            };
          }
        }
        return node;
      }
      return item;
    });

  const getSelphiConfig = () =>
    (config?.[WidgetNameList.selphi] || {}) as Authentication;

  const getSelphidConfig = () =>
    (config?.[WidgetNameList.selphid] || {}) as OnboardingDemo;

  return {
    loading,
    config: {
      previewCapture: getSelphidConfig().previewCapture,
      country: getSelphidConfig().country,
      logImages: false,
      className: '',
      language: '',
      licenseKey: getSelphidConfig().licenseKey,
      documentType: getSelphidConfig().documentType,
      cameraHeight: getSelphidConfig().cameraHeight,
      cameraWidth: getSelphidConfig().cameraWidth,
    },
    getWorkflow,
  };
}
